/**
 * src/components/Financial/AspectSearchSection.jsx
 * 【プロフェッショナル統計分析 ＋ グラフ描画 ＋ ページネーション修正済】
 */

import React, { useState, useMemo, useEffect, useRef } from "react";
import * as Astro from "../../astronomy.js"; // searchAspectsExtended を Astro から取得するように変更
import { parseInvestingCSV } from "../../utils/csvParser.js";
import { styles as commonStyles } from "../../utils/styles.js";
import { PLANET_SYMBOLS } from "../../utils/constants.js";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Upload,
  FileText,
  X,
  TrendingUp,
  BarChart2,
  Activity,
  Info,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Calendar,
  Database,
  Copy,
  LineChart as LineChartIcon, // アイコン名の衝突を避けるためエイリアス
} from "lucide-react";
// =============================================================================
// 1. 高度統計計算・ユーティリティ関数
// =============================================================================

const PLANET_DATA_MAP = {
  Sun: { s: "☉", n: "太陽" },
  Moon: { s: "☽", n: "月" },
  Mercury: { s: "☿", n: "水星" },
  Venus: { s: "♀", n: "金星" },
  Earth: { s: "⊕", n: "地球" },
  Mars: { s: "♂", n: "火星" },
  Jupiter: { s: "♃", n: "木星" },
  Saturn: { s: "♄", n: "土星" },
  Uranus: { s: "♅", n: "天王星" },
  Neptune: { s: "♆", n: "海王星" },
  Pluto: { s: "♇", n: "冥王星" },
  NorthNode: { s: "☊", n: "ノード" },
};

/** 基本統計計算 */
const calcBasicStats = function (values) {
  if (!values || values.length === 0)
    return { mean: 0, median: 0, std: 0, winRate: 0, count: 0 };
  var sorted = values.slice().sort(function (a, b) {
    return a - b;
  });
  var sum = values.reduce(function (a, b) {
    return a + b;
  }, 0);
  var mean = sum / values.length;
  var median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];
  var sqDiff = values.map(function (v) {
    return Math.pow(v - mean, 2);
  });
  var std = Math.sqrt(
    sqDiff.reduce(function (a, b) {
      return a + b;
    }, 0) / values.length,
  );
  var wins = values.filter(function (v) {
    return v > 0;
  }).length;
  return {
    mean: mean,
    median: median,
    std: std,
    winRate: (wins / values.length) * 100,
    count: values.length,
  };
};

/** t検定 (p値近似) */
const runTTest = function (values) {
  var stats = calcBasicStats(values);
  if (stats.std === 0 || values.length < 2) return { t: 0, p: 1 };
  var t = stats.mean / (stats.std / Math.sqrt(values.length));
  var p = 2 * (1 - 1 / (1 + Math.exp(-0.717 * Math.pow(Math.abs(t), 1.3))));
  return { t: t, p: p };
};

/** ブートストラップ 95%信頼区間 */
const runBootstrap = function (values, iterations) {
  if (!values || values.length < 5) return null;
  var means = [];
  for (var i = 0; i < iterations; i++) {
    var resSum = 0;
    for (var j = 0; j < values.length; j++) {
      resSum += values[Math.floor(Math.random() * values.length)];
    }
    means.push(resSum / values.length);
  }
  means.sort(function (a, b) {
    return a - b;
  });
  return {
    low: means[Math.floor(iterations * 0.025)],
    high: means[Math.floor(iterations * 0.975)],
  };
};

/** 最大ドローダウン (累積リターンベース) */
const calcMaxDD = function (results, offset) {
  if (!results || results.length === 0) return 0;
  var peak = -Infinity;
  var maxDD = 0;
  var current = 0;
  results.forEach(function (r) {
    if (r.priceStats && r.priceStats.returns[offset] !== null) {
      current += r.priceStats.returns[offset];
      if (current > peak) peak = current;
      var dd = peak - current;
      if (dd > maxDD) maxDD = dd;
    }
  });
  return maxDD;
};

/** 価格データ検索 */
const getPriceAtDate = function (targetDate, csvData) {
  if (!csvData || csvData.length === 0) return null;
  const targetTs = new Date(
    Date.UTC(
      targetDate.getUTCFullYear(),
      targetDate.getUTCMonth(),
      targetDate.getUTCDate(),
    ),
  ).getTime();
  let closest = null;
  let minDiff = Infinity;
  for (let i = 0; i < csvData.length; i++) {
    const diff = targetTs - csvData[i].timestamp;
    if (diff >= 0 && diff < minDiff) {
      minDiff = diff;
      closest = csvData[i];
    }
    if (diff === 0) break;
  }
  return closest && minDiff <= 86400000 * 5 ? closest : null;
};

/** SquareのWAX/WAN判定用ヘルパー関数 */
const getSquareLabel = (p1, lon1, p2, lon2) => {
  if (
    lon1 === undefined ||
    lon2 === undefined ||
    lon1 === null ||
    lon2 === null
  )
    return "";

  const order = [
    "Moon",
    "Mercury",
    "Venus",
    "Sun",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];
  const idx1 = order.indexOf(p1);
  const idx2 = order.indexOf(p2);

  let fastLon = lon1,
    slowLon = lon2;
  // インデックスが小さい方が足が速い（Moon=0, Pluto=9）
  // idx1 < idx2 の場合、p1の方が足が速い
  if (idx1 !== -1 && idx2 !== -1 && idx1 > idx2) {
    fastLon = lon2;
    slowLon = lon1;
  }

  const phase = (fastLon - slowLon + 360) % 360;
  return phase < 180 ? "(WAX)" : "(WAN)";
};

// =============================================================================
// 2. メインコンポーネント
// =============================================================================

// =============================================================================
// 2. メインコンポーネント
// =============================================================================

// =============================================================================
// 2. メインコンポーネント
// =============================================================================

// =============================================================================
// 2. メインコンポーネント
// =============================================================================

export default function AspectSearchSection(props) {
  // --- 1. ダークモード判定とレスポンシブ判定 (標準記述) ---
  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      ) {
        return true;
      }
    }
    return false;
  })();

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  useEffect(function () {
    const handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isMobile = windowWidth < 768;

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#64748b",
    subText: isDark ? "#9ca3af" : "#64748b",
    accent: "#2c2d96",
  };

  const planetColors = props.planetColors || {};
  const aspectSettings = props.aspectSettings || {
    majorOrb: 8,
    minorOrb: 2,
    outerPlanetOrb: 1,
    useOuterLimit: false,
  };

  // --- 2. 検索・結果・ページネーション設定 ---
  const [planet1, setPlanet1] = useState("Any");
  const [planet2, setPlanet2] = useState("Any");
  const [aspectType, setAspectType] = useState("major");
  const [fromYear, setFromYear] = useState(new Date().getFullYear() - 10);
  const [fromMonth, setFromMonth] = useState(1);
  const [toYear, setToYear] = useState(new Date().getFullYear());
  const [toMonth, setToMonth] = useState(12);
  const [signFilter, setSignFilter] = useState("すべて");
  const [rxFilter, setRxFilter] = useState("Any");

  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  // --- 3. CSVデータ管理と永続化 (LocalStorage) ---
  const [csvData, setCsvData] = useState([]);
  const [chartName, setChartName] = useState("未設定");
  const fileInputRef = useRef(null);

  // 保存されたデータの自動復元
  useEffect(function () {
    if (typeof localStorage !== "undefined") {
      const savedCsv = localStorage.getItem("aspect_search_saved_csv");
      const savedName = localStorage.getItem("aspect_search_saved_name");
      if (savedCsv !== null && savedName !== null) {
        try {
          setCsvData(JSON.parse(savedCsv));
          setChartName(savedName);
        } catch (e) {
          console.error("Failed to parse saved CSV", e);
        }
      }
    }
  }, []);

  /**
   * 惑星リストの動的定義：ヘリオセントリック対応版
   */
  const availablePlanets = useMemo(
    function () {
      const isHelio =
        props.isHeliocentric === true || props.isHeliocentric === "true";

      if (isHelio) {
        // ヘリオセントリック：地球を含み、太陽・月を除外
        return [
          "Earth",
          "Mercury",
          "Venus",
          "Mars",
          "Jupiter",
          "Saturn",
          "Uranus",
          "Neptune",
          "Pluto",
        ];
      }
      // ジオセントリック：標準リスト
      return [
        "Sun",
        "Moon",
        "Mercury",
        "Venus",
        "Mars",
        "Jupiter",
        "Saturn",
        "Uranus",
        "Neptune",
        "Pluto",
      ];
    },
    [props.isHeliocentric],
  );

  // ファイルアップロードと保存処理
  const handleFileUpload = function (e) {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = function (event) {
      if (
        event !== null &&
        event.target !== null &&
        event.target.result !== null
      ) {
        const parsed = parseInvestingCSV(event.target.result);
        if (parsed !== null && parsed.length > 0) {
          const cleanName = file.name.replace(".csv", "");
          setCsvData(parsed);
          setChartName(cleanName);

          if (typeof localStorage !== "undefined") {
            localStorage.setItem(
              "aspect_search_saved_csv",
              JSON.stringify(parsed),
            );
            localStorage.setItem("aspect_search_saved_name", cleanName);
          }
          alert(parsed.length + "件のデータを読み込みました。");
        }
      }
    };
    reader.readAsText(file);
  };

  // --- 4. スタイル定義 (二重宣言エラー防止のためここに集約) ---
  // フォーム部分もダークモードを完全に反映
  const selectStyle = {
    padding: "10px",
    borderRadius: "10px",
    border: "1px solid " + theme.border,
    fontSize: "13px",
    background: isDark ? "#2d2c33" : "#ffffff", // ダークモード時の背景色を #2d2c33 に修正
    color: theme.text,
    outline: "none",
    width: "100%",
  };
  const labelStyle = {
    fontSize: "11px",
    color: theme.subText,
    fontWeight: "bold",
    marginBottom: "4px",
    display: "block",
  };

  /** 検索実行：アスペクト検出および価格データ紐付けの完全版 */
  const handleSearch = function () {
    // --- 検索期間のバリデーション (1800-2100) を追加 ---
    if (fromYear < 1800 || fromYear > 2100 || toYear < 1800 || toYear > 2100) {
      alert("検索期間は1800年から2100年の範囲内で指定してください。");
      return;
    }

    setIsSearching(true);
    setCurrentPage(1);
    setTimeout(function () {
      try {
        const fromDate = new Date(fromYear, fromMonth - 1, 1);
        const toDate = new Date(toYear, toMonth, 0, 23, 59, 59);

        // プロパティの正規化と検索実行
        const rawResults = Astro.searchAspectsExtended({
          planet1: planet1 === "Any" ? null : planet1,
          planet2: planet2 === "Any" ? null : planet2,
          aspectType: aspectType,
          fromDate: fromDate,
          toDate: toDate,
          signFilter: signFilter === "すべて" ? null : signFilter,
          rxFilter: rxFilter === "Any" ? null : rxFilter,
          isHeliocentric:
            props.isHeliocentric === true || props.isHeliocentric === "true",
          isSidereal: props.isSidereal === true || props.isSidereal === "true",
          aspectSettings: aspectSettings,
        });

        // --- 修正：同一アスペクトの重複を排除し、Exact（最小オーブ）のみ抽出 ---
        const filtered = [];
        if (rawResults && rawResults.length > 0) {
          // 1. 日付順にソート
          rawResults.sort(function (a, b) {
            return a.date.getTime() - b.date.getTime();
          });

          // 2. [天体1, アスペクト名, 天体2] でグループ化
          const groups = {};
          rawResults.forEach(function (hit) {
            const key = [hit.planet1, hit.aspect.name, hit.planet2]
              .sort()
              .join("|");
            if (!groups[key]) groups[key] = [];
            groups[key].push(hit);
          });

          // 3. 各グループ内で30日以内の連続ヒットを1つのイベントとし、最小オーブを採用
          Object.keys(groups).forEach(function (key) {
            const hits = groups[key];
            const THRESHOLD = 30; // 30日以内は同一の形成とみなす

            let currentGroup = [hits[0]];
            for (let i = 1; i < hits.length; i++) {
              const prev = hits[i - 1];
              const curr = hits[i];
              const diffDays =
                (curr.date.getTime() - prev.date.getTime()) /
                (1000 * 60 * 60 * 24);

              if (diffDays <= THRESHOLD) {
                currentGroup.push(curr);
              } else {
                const exact = currentGroup.reduce(function (m, h) {
                  return h.orb < m.orb ? h : m;
                }, currentGroup[0]);
                filtered.push(exact);
                currentGroup = [curr];
              }
            }
            const finalExact = currentGroup.reduce(function (m, h) {
              return h.orb < m.orb ? h : m;
            }, currentGroup[0]);
            filtered.push(finalExact);
          });
        }
        filtered.sort(function (a, b) {
          return a.date.getTime() - b.date.getTime();
        });

        // 検索結果に価格データを紐付ける
        // 修正: -30日～+30日の連続データを取得し、グラフ描画と詳細分析に対応
        const peaks = filtered.map(function (hit) {
          // 天体位置の計算
          const isHelio =
            props.isHeliocentric === true || props.isHeliocentric === "true";
          hit.p1Lon = Astro.getPlanetLongitude(hit.planet1, hit.date, isHelio);
          hit.p2Lon = Astro.getPlanetLongitude(hit.planet2, hit.date, isHelio);

          if (csvData && csvData.length > 0) {
            // 基準日(Event Day)の価格
            const base = getPriceAtDate(hit.date, csvData);

            if (base && base.Price !== undefined) {
              const stats = {
                basePrice: base.Price,
                returns: {}, // 互換性維持のため
                dailyData: {}, // 日次データ格納用: { dayOffset: { price, cumRet, logRet } }
              };

              // -30日から+30日までループ
              for (let d = -30; d <= 30; d++) {
                if (d === 0) {
                  stats.returns[0] = 0;
                  stats.dailyData[0] = {
                    price: base.Price,
                    cumRet: 0,
                    logRet: 0,
                  };
                } else {
                  const targetDate = new Date(
                    hit.date.getTime() + d * 86400000,
                  );
                  const target = getPriceAtDate(targetDate, csvData); // 土日補正は getPriceAtDate 内で処理済

                  if (target && target.Price !== undefined) {
                    const cumRet =
                      ((target.Price - base.Price) / base.Price) * 100;
                    // 対数リターン: ln(Pt / P0) * 100
                    const logRet = Math.log(target.Price / base.Price) * 100;

                    stats.returns[d] = cumRet; // 既存ロジック互換
                    stats.dailyData[d] = {
                      price: target.Price,
                      cumRet: cumRet,
                      logRet: logRet,
                    };
                  } else {
                    stats.returns[d] = null;
                    stats.dailyData[d] = null;
                  }
                }
              }
              hit.priceStats = stats;
            }
          }
          return hit;
        });

        // 日付順（昇順）にソートしてステートを更新
        peaks.sort(function (a, b) {
          return a.date.getTime() - b.date.getTime();
        });

        setResults(peaks);
      } catch (e) {
        console.error("Aspect Search Logic Error:", e);
        setResults([]);
      }
      setIsSearching(false);
    }, 100);
  };

  // 検索結果のコピー機能
  const copyResultsToClipboard = function () {
    if (results.length === 0) return;
    const headers = "Date,Time,Planet1,Aspect,Planet2,Orb\n";
    const rows = results
      .map(function (r) {
        return (
          r.date.toLocaleDateString("ja-JP") +
          "," +
          r.date.toLocaleTimeString("ja-JP", {
            hour: "2-digit",
            minute: "2-digit",
          }) +
          "," +
          r.planet1 +
          "," +
          r.aspect.name +
          "," +
          r.planet2 +
          "," +
          r.orb.toFixed(2)
        );
      })
      .join("\n");
    navigator.clipboard.writeText(headers + rows);
    alert("検索結果をクリップボードにコピーしました");
  };

  // 検索結果のCSVダウンロード機能
  const downloadResultsCSV = function () {
    if (results.length === 0) return;
    const headers = "Date,Time,Planet1,Aspect,Planet2,Orb\n";
    const rows = results
      .map(function (r) {
        return (
          r.date.toLocaleDateString("ja-JP") +
          "," +
          r.date.toLocaleTimeString("ja-JP", {
            hour: "2-digit",
            minute: "2-digit",
          }) +
          "," +
          r.planet1 +
          "," +
          r.aspect.name +
          "," +
          r.planet2 +
          "," +
          r.orb.toFixed(2)
        );
      })
      .join("\n");
    const blob = new Blob([headers + rows], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "aspect_search_results.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /** 統計ダッシュボード用計算 (修正: グラフ用日次集計 + 指定フォーマットのテーブル集計) */
  const summaryData = useMemo(
    function () {
      if (!results || results.length === 0) return null;

      var validResults = results.filter(function (r) {
        return r.priceStats && r.priceStats.dailyData;
      });
      if (validResults.length === 0) return null;

      // 1. テーブル表示用データ集計 (当日, ±1, ±3, ±7, ±15, ±30)
      const targetOffsets = [-30, -15, -7, -3, -1, 0, 1, 3, 7, 15, 30];
      const tableData = {};

      targetOffsets.forEach(function (off) {
        const statsList = validResults
          .map((r) => r.priceStats.dailyData[off])
          .filter((d) => d !== null && d !== undefined);

        if (statsList.length > 0) {
          // 累積リターン平均
          const sumCum = statsList.reduce((a, b) => a + b.cumRet, 0);
          const meanCum = sumCum / statsList.length;

          // 対数リターン平均
          const sumLog = statsList.reduce((a, b) => a + b.logRet, 0);
          const meanLog = sumLog / statsList.length;

          // 勝率 (累積リターン > 0 の割合)
          const wins = statsList.filter((d) => d.cumRet > 0).length;
          const winRate = (wins / statsList.length) * 100;

          tableData[off] = {
            meanCum: meanCum,
            meanLog: meanLog,
            winRate: winRate,
            count: statsList.length,
          };
        }
      });

      // 2. グラフ描画用データ集計 (-30d ～ +30d 全期間)
      // chartData: [{ day, avgCum, traces: [val, val...] }, ...]
      const chartData = [];
      const days = [];
      for (let d = -30; d <= 30; d++) days.push(d);

      days.forEach(function (day) {
        const dailyVals = validResults
          .map((r) => r.priceStats.dailyData[day])
          .filter((v) => v !== null && v !== undefined)
          .map((v) => v.cumRet);

        if (dailyVals.length > 0) {
          const avg = dailyVals.reduce((a, b) => a + b, 0) / dailyVals.length;
          chartData.push({
            day: day,
            avgCum: avg,
            traces: dailyVals, // 個別ライン描画用（重い場合は間引きが必要だが、ここでは全保持）
          });
        } else {
          chartData.push({ day: day, avgCum: 0, traces: [] });
        }
      });

      // 3. トレンド反転分析 (既存ロジック維持)
      var reversalCount = 0;
      var continuationCount = 0;
      var totalValidForTrend = 0;

      validResults.forEach(function (r) {
        if (r.priceStats.dailyData[-30] && r.priceStats.dailyData[30]) {
          // 前半トレンド: (P0 / P-30) - 1
          // dailyDataのcumRetは (Pt - P0)/P0 * 100 なので、Pt/P0 = cumRet/100 + 1
          // P0/P-30 = 1 / (P-30/P0) = 1 / (stats[-30].cumRet/100 + 1)
          var retM30 = r.priceStats.dailyData[-30].cumRet / 100;
          var preTrend = 1 / (retM30 + 1) - 1;

          var retP30 = r.priceStats.dailyData[30].cumRet / 100;
          var postTrend = retP30;

          if (preTrend !== 0 && postTrend !== 0) {
            totalValidForTrend++;
            if (
              (preTrend > 0 && postTrend < 0) ||
              (preTrend < 0 && postTrend > 0)
            ) {
              reversalCount++;
            } else {
              continuationCount++;
            }
          }
        }
      });

      return {
        tableData: tableData,
        chartData: chartData,
        trendAnalysis: {
          reversalRate:
            totalValidForTrend > 0
              ? (reversalCount / totalValidForTrend) * 100
              : 0,
          continuationRate:
            totalValidForTrend > 0
              ? (continuationCount / totalValidForTrend) * 100
              : 0,
          sampleSize: totalValidForTrend,
        },
        // リスク評価用（最大暴落）
        maxEventLoss: Math.min(...chartData.map((d) => d.avgCum)), // 平均線の最小値を簡易リスク指標とする
      };
    },
    [results],
  );

  // --- ページネーション用変数 ---
  const paginatedResults = useMemo(
    function () {
      if (!results) return [];
      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      return results.slice(start, start + ITEMS_PER_PAGE);
    },
    [results, currentPage],
  );

  const totalPages = results ? Math.ceil(results.length / ITEMS_PER_PAGE) : 0;

  const pageBtnSt = {
    background: theme.cardBg,
    border: "1px solid " + theme.border,
    color: theme.text,
    padding: "10px",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        borderRadius: "20px",
        padding: isMobile ? "20px" : "35px",
        // 紫の外枠を完全に排除
        border: "1px solid " + theme.border,
        boxSizing: "border-box",
        width: "100%",
        minHeight: "850px",
      }}
    >
      {/* 1. ヘッダー：チャート分析セクションとデザインを完全統合 */}
      {/* 1. ヘッダー：チャート分析セクションとデザインを完全統合 */}
      <div style={{ marginBottom: "30px" }}>
        <h3
          style={{
            margin: "0 0 20px 0",
            fontSize: "18px",
            fontWeight: "900",
            color: isDark ? "#cbd5e1" : "#64748b",
            letterSpacing: "0.02em",
          }}
        >
          天体価格統計ダッシュボード
        </h3>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {/* 修正：モバイル時は銘柄追加ボタンを非表示にする */}
          {!isMobile && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                color: "#ffffff",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: "bold",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 4px 12px rgba(79, 70, 229, 0.2)",
              }}
            >
              <Upload size={16} />
              <span>銘柄を追加 (CSV)</span>
              <input
                type="file"
                onChange={handleFileUpload}
                accept=".csv"
                style={{ display: "none" }}
              />
            </label>
          )}

          {/* ファイル名タグ：アクティブ色 (#2c2d96) と Xボタンを統一 */}
          {chartName !== "未設定" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 16px",
                background: "#2c2d96", // チャート分析のアクティブタグと同じ色
                borderRadius: "10px",
                border: "1px solid #4f46e5",
                color: "#ffffff",
              }}
            >
              <FileText size={16} color="#a5b4fc" />
              <span style={{ fontSize: "13px", fontWeight: "bold" }}>
                {chartName}
              </span>
              <button
                onClick={() => {
                  setCsvData([]);
                  setChartName("未設定");
                  localStorage.removeItem("aspect_search_saved_csv");
                  localStorage.removeItem("aspect_search_saved_name");
                }}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 2,
                  display: "flex",
                  alignItems: "center",
                  opacity: 0.8,
                }}
              >
                <X size={14} color="#ffffff" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 検索パネル：動的な惑星リストとダークモード配色を適用 */}
      <div
        style={{
          background: theme.subBg,
          // 修正：PCは 30px、モバイルは 10px に切り替え
          padding: isMobile ? "10px" : "30px",
          borderRadius: "16px",
          border: "1px solid " + theme.border,
          marginBottom: "35px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          {/* 惑星1選択 */}
          <div>
            <span style={labelStyle}>惑星1</span>
            <select
              value={planet1}
              onChange={(e) => setPlanet1(e.target.value)}
              style={selectStyle}
            >
              <option value="Any">すべて</option>
              {availablePlanets.map((p) => (
                <option key={"p1-" + p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {/* アスペクト選択 */}
          {/* アスペクト選択 (拡充版) */}
          <div>
            <span style={labelStyle}>アスペクト</span>
            <select
              value={aspectType}
              onChange={(e) => setAspectType(e.target.value)}
              style={selectStyle}
            >
              <option value="major">主要アスペクト (Major)</option>
              <option value="hard">ハードアスペクト (Hard)</option>
              <option value="all">すべて表示</option>

              {/* 主要5アスペクトを30°系の上に追加 */}
              <optgroup label="主要アスペクト (Individual)">
                <option value="Conjunction">Conjunction 0°</option>
                <option value="Sextile">Sextile 60°</option>
                <option value="Square">Square 90°</option>
                <option value="Trine">Trine 120°</option>
                <option value="Opposition">Opposition 180°</option>
              </optgroup>

              <optgroup label="30°系">
                <option value="Semi-Sextile">Semi-Sextile 30°</option>
                <option value="Quincunx">Quincunx / Inconjunct 150°</option>
              </optgroup>

              <optgroup label="36°系 (10分割 / 108°含む)">
                <option value="Decile">Decile 36°</option>
                <option value="Bi-Decile">Bi-Decile 72°</option>
                <option value="Tri-Decile">Tri-Decile 108°</option>
                <option value="Quadra-Decile">Quadra-Decile 144°</option>
              </optgroup>

              <optgroup label="40°系 (9分割 / 160°含む)">
                <option value="Novile">Novile 40°</option>
                <option value="Bi-Novile">Bi-Novile 80°</option>
                <option value="Quad-Novile">Quad-Novile 160°</option>
              </optgroup>

              <optgroup label="45°系">
                <option value="Semi-Square">Semi-Square 45°</option>
                <option value="Sesquiquadrate">Sesqui-Square 135°</option>
              </optgroup>

              <optgroup label="51°系 (7分割)">
                <option value="Septile">Septile 51.4°</option>
                <option value="Bi-Septile">Bi-Septile 102.9°</option>
                <option value="Tri-Septile">Tri-Septile 154.3°</option>
              </optgroup>

              <optgroup label="72°系 (5分割)">
                <option value="Quintile">Quintile 72°</option>
                <option value="Bi-Quintile">Bi-Quintile 144°</option>
              </optgroup>

              <optgroup label="165°系 (11分割)">
                <option value="Undecile">Undecile 165°</option>
              </optgroup>

              <optgroup label="その他">
                <option value="Semi-Decile">Semi-Decile 18°</option>
                <option value="Semi-Novile">Semi-Novile 20°</option>
              </optgroup>
            </select>
          </div>

          {/* 惑星2選択 */}
          <div>
            <span style={labelStyle}>惑星2</span>
            <select
              value={planet2}
              onChange={(e) => setPlanet2(e.target.value)}
              style={selectStyle}
            >
              <option value="Any">すべて</option>
              {availablePlanets.map((p) => (
                <option key={"p2-" + p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: "15px",
            marginBottom: "25px",
          }}
        >
          <div>
            <span style={labelStyle}>FROM</span>
            <input
              type="number"
              value={fromYear}
              onChange={(e) => setFromYear(parseInt(e.target.value))}
              style={{ width: "100%", ...selectStyle }}
            />
          </div>
          <div>
            <span style={labelStyle}>TO</span>
            <input
              type="number"
              value={toYear}
              onChange={(e) => setToYear(parseInt(e.target.value))}
              style={{ width: "100%", ...selectStyle }}
            />
          </div>
          <div>
            <span style={labelStyle}>星座</span>
            <select
              value={signFilter}
              onChange={(e) => setSignFilter(e.target.value)}
              style={{ width: "100%", ...selectStyle }}
            >
              <option value="すべて">すべて</option>
              {[
                "Aries",
                "Taurus",
                "Gemini",
                "Cancer",
                "Leo",
                "Virgo",
                "Libra",
                "Scorpio",
                "Sagittarius",
                "Capricorn",
                "Aquarius",
                "Pisces",
              ].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span style={labelStyle}>逆行</span>
            <select
              value={rxFilter}
              onChange={(e) => setRxFilter(e.target.value)}
              style={{ width: "100%", ...selectStyle }}
            >
              <option value="Any">すべて</option>
              <option value="Yes">逆行のみ</option>
              <option value="No">順行のみ</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleSearch}
          disabled={isSearching}
          style={{
            width: "100%",
            padding: "18px",
            background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
            border: "none",
            borderRadius: "12px",
            fontWeight: "bold",
            cursor: "pointer",
            opacity: isSearching ? 0.6 : 1,
            fontSize: "16px",
            boxShadow: "0 4px 15px rgba(102,126,234,0.3)",
          }}
        >
          {isSearching ? "高度統計エンジン実行中..." : "アスペクト分析を実行 →"}
        </button>
      </div>

      {/* 3. 統計サマリーダッシュボード */}
      {summaryData && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "35px",
            marginBottom: "50px",
          }}
        >
          {/* トレンド反転分析パネル (★新規追加) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "25px",
            }}
          >
            <div
              style={{
                background: isDark ? "#2d2c33" : "#ffffff",
                padding: "25px",
                borderRadius: "16px",
                border: "1px solid " + theme.border,
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#a78bfa",
                  marginBottom: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <TrendingUp size={18} /> トレンド反転確率分析 (Pre-30d vs
                Post-30d)
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  height: "100px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: "#8b5cf6",
                    }}
                  >
                    {summaryData.trendAnalysis.reversalRate.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                    トレンド反転 (Reversal)
                  </div>
                </div>
                <div
                  style={{
                    width: "2px",
                    height: "40px",
                    background: "rgba(255,255,255,0.1)",
                  }}
                ></div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: "#94a3b8",
                    }}
                  >
                    {summaryData.trendAnalysis.continuationRate.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                    トレンド継続 (Continuation)
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                background: theme.cardBg,
                padding: "25px",
                borderRadius: "16px",
                border: "1px solid " + theme.border,
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginBottom: "15px",
                  color: theme.text,
                }}
              >
                リスク評価
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px dashed " + theme.border,
                    paddingBottom: "8px",
                  }}
                >
                  <span style={{ fontSize: "12px", color: theme.subText }}>
                    単日最大暴落率 (期間中)
                  </span>
                  <span style={{ fontWeight: "bold", color: "#ef4444" }}>
                    {summaryData[30]
                      ? summaryData[30].maxEventLoss.toFixed(2)
                      : "0.00"}
                    %
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingBottom: "8px",
                  }}
                >
                  <span style={{ fontSize: "12px", color: theme.subText }}>
                    トレンド反転優位性
                  </span>
                  <span
                    style={{
                      fontWeight: "bold",
                      color:
                        summaryData.trendAnalysis.reversalRate > 55
                          ? "#8b5cf6"
                          : theme.text,
                    }}
                  >
                    {summaryData.trendAnalysis.reversalRate > 55
                      ? "高（反転暗示）"
                      : "並（ランダム）"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 統計解析テーブル */}
          <div
            style={{
              background: theme.cardBg,
              borderRadius: "16px",
              border: "1px solid " + theme.border,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "15px 20px",
                borderBottom: "1px solid " + theme.border,
                background: theme.subBg,
                fontWeight: "bold",
                fontSize: "14px",
                color: theme.text,
              }}
            >
              【統計分析レポート：天体イベント影響度】
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  textAlign: "center", // 中央揃えで見やすく
                  color: theme.text,
                }}
              >
                <thead>
                  <tr
                    style={{
                      color: theme.subText,
                      borderBottom: "2px solid " + theme.border,
                      fontSize: "11px",
                      textTransform: "uppercase",
                    }}
                  >
                    <th style={{ padding: "12px", textAlign: "left" }}>
                      期間 (Days)
                    </th>
                    <th>累積リターン集計 (Avg)</th>
                    <th>対数リターン平均 (Log)</th>
                    <th>勝率 (Win Rate)</th>
                    <th>サンプル数</th>
                  </tr>
                </thead>
                <tbody>
                  {/* 指定された順序で表示: 0, ±1, ±3, ±7, ±15, ±30 => つまり時系列順 */}
                  {[-30, -15, -7, -3, -1, 0, 1, 3, 7, 15, 30].map(
                    function (off) {
                      const s = summaryData.tableData[off];
                      if (!s) return null;

                      // 0日は基準点なので特別扱い
                      const isZero = off === 0;

                      return (
                        <tr
                          key={off}
                          style={{
                            borderBottom: "1px solid " + theme.border,
                            color: theme.text,
                            background: isZero
                              ? isDark
                                ? "rgba(79, 70, 229, 0.1)"
                                : "#eff6ff"
                              : "transparent",
                          }}
                        >
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: "bold",
                            }}
                          >
                            {isZero
                              ? "Event Day (0)"
                              : off > 0
                                ? "+" + off + " days"
                                : off + " days"}
                          </td>
                          <td
                            style={{
                              color: s.meanCum >= 0 ? "#22c55e" : "#ef4444",
                              fontWeight: "bold",
                            }}
                          >
                            {isZero
                              ? "---"
                              : (s.meanCum >= 0 ? "+" : "") +
                                s.meanCum.toFixed(2) +
                                "%"}
                          </td>
                          <td style={{ color: theme.text }}>
                            {isZero ? "---" : s.meanLog.toFixed(4)}
                          </td>
                          <td
                            style={{
                              color:
                                s.winRate >= 50 ? "#8b5cf6" : theme.subText,
                              fontWeight: s.winRate >= 50 ? "bold" : "normal",
                            }}
                          >
                            {isZero ? "---" : s.winRate.toFixed(1) + "%"}
                          </td>
                          <td
                            style={{ fontSize: "11px", color: theme.subText }}
                          >
                            {s.count}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 新規追加: 平均累積リターンチャート (SVG実装・レイアウト修正版) */}
          <div
            style={{
              background: theme.cardBg,
              borderRadius: "16px",
              border: "1px solid " + theme.border,
              padding: "20px",
              marginTop: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "20px",
              }}
            >
              <LineChartIcon size={18} color={theme.text} />
              <span
                style={{
                  fontWeight: "bold",
                  color: theme.text,
                  fontSize: "14px",
                }}
              >
                累積リターン（イベント前後30日間）
              </span>
            </div>

            {/* チャート描画エリア */}
            <div
              style={{ width: "100%", height: "300px", position: "relative" }}
            >
              {(() => {
                const data = summaryData.chartData;
                if (!data || data.length === 0) return <div>No Data</div>;

                // --- レイアウト設定 ---
                const svgW = 600; // 内部座標系の幅
                const svgH = 300; // 内部座標系の高さ
                const padTop = 20;
                const padBottom = 30;
                const padLeft = 40;
                const padRight = 20;

                const chartW = svgW - padLeft - padRight;
                const chartH = svgH - padTop - padBottom;

                // --- データ範囲計算 ---
                // 平均線の最大・最小を取得
                const avgValues = data.map((d) => d.avgCum);
                let maxVal = Math.max(...avgValues);
                let minVal = Math.min(...avgValues);

                // 範囲が0の場合の対策 & 上下に余裕を持たせる (レンジの20%程度)
                let range = maxVal - minVal;
                if (range === 0) range = 1;
                const margin = range * 0.2;
                const yMax = maxVal + margin;
                const yMin = minVal - margin;
                const yRange = yMax - yMin;

                // --- 座標変換ヘルパー ---
                // xIndex: 0..60
                const getX = (index) => padLeft + (index / 60) * chartW;
                const getY = (val) => {
                  const normalized = (val - yMin) / yRange;
                  // Y軸は上が0、下がheightなので反転させる
                  return padTop + chartH - normalized * chartH;
                };

                // --- パス生成 ---
                // 1. 個別ライン (Faint Lines) - 重くなりすぎないよう間引きつつ描画
                const individualPaths = [];
                const tracesCount = data[0].traces.length;
                // 最大100本程度に制限して描画負荷を下げる
                const step = Math.max(1, Math.floor(tracesCount / 100));

                for (let i = 0; i < tracesCount; i += step) {
                  let pathD = "";
                  let valid = true;
                  for (let d = 0; d < data.length; d++) {
                    const val = data[d].traces[i];
                    if (val === undefined || isNaN(val)) {
                      valid = false;
                      break;
                    }

                    const x = getX(d);
                    // 描画エリア外にはみ出さないようクリッピング（簡易）
                    let y = getY(val);
                    y = Math.max(padTop, Math.min(padTop + chartH, y));

                    pathD +=
                      (d === 0 ? "M" : "L") +
                      ` ${x.toFixed(1)} ${y.toFixed(1)}`;
                  }
                  if (valid) {
                    individualPaths.push(
                      <path
                        key={`trace-${i}`}
                        d={pathD}
                        fill="none"
                        stroke={
                          isDark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)"
                        }
                        strokeWidth="1"
                      />,
                    );
                  }
                }

                // 2. 平均線 (Bold Red)
                let avgPathD = "";
                for (let d = 0; d < data.length; d++) {
                  const x = getX(d);
                  const y = getY(data[d].avgCum);
                  avgPathD +=
                    (d === 0 ? "M" : "L") + ` ${x.toFixed(1)} ${y.toFixed(1)}`;
                }

                // 3. ゼロライン (Event Day: index 30)
                const zeroX = getX(30);
                // リターン0%のライン
                const zeroY = getY(0);
                const showZeroLine = 0 >= yMin && 0 <= yMax;

                return (
                  <svg
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${svgW} ${svgH}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ overflow: "visible" }}
                  >
                    {/* 背景エリア(デバッグ用あるいは視認性向上用) */}
                    <rect
                      x={padLeft}
                      y={padTop}
                      width={chartW}
                      height={chartH}
                      fill="transparent"
                    />

                    {/* リターン0%ライン (水平) */}
                    {showZeroLine && (
                      <line
                        x1={padLeft}
                        y1={zeroY}
                        x2={svgW - padRight}
                        y2={zeroY}
                        stroke={theme.subText}
                        strokeWidth="1"
                        strokeDasharray="4,4"
                        opacity="0.5"
                      />
                    )}

                    {/* イベント日ライン (垂直) */}
                    <line
                      x1={zeroX}
                      y1={padTop}
                      x2={zeroX}
                      y2={svgH - padBottom}
                      stroke="#949494"
                      strokeWidth="1"
                      strokeDasharray="4,2"
                      opacity="0.8"
                    />

                    {/* 個別ライン描画 */}
                    {individualPaths}

                    {/* 平均線描画 */}
                    <path
                      d={avgPathD}
                      fill="none"
                      stroke="#efd544"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))",
                      }}
                    />

                    {/* --- 軸ラベル --- */}
                    {/* X軸: -30, 0, +30 */}
                    <g fontSize="10" fill={theme.subText} textAnchor="middle">
                      <text x={getX(0)} y={svgH - 10}>
                        -30d
                      </text>
                      <text
                        x={getX(30)}
                        y={svgH - 10}
                        fill="#ef4444"
                        fontWeight="bold"
                      >
                        Event (0)
                      </text>
                      <text x={getX(60)} y={svgH - 10}>
                        +30d
                      </text>
                    </g>

                    {/* Y軸: Max, 0, Min */}
                    <g fontSize="10" fill={theme.subText} textAnchor="end">
                      <text x={padLeft - 6} y={padTop + 4}>
                        {yMax.toFixed(1)}%
                      </text>
                      {showZeroLine &&
                        Math.abs(zeroY - padTop) > 20 &&
                        Math.abs(zeroY - (padTop + chartH)) > 20 && (
                          <text x={padLeft - 6} y={zeroY + 4}>
                            0%
                          </text>
                        )}
                      <text x={padLeft - 6} y={padTop + chartH}>
                        {yMin.toFixed(1)}%
                      </text>
                    </g>

                    {/* 枠線 (グラフエリア) */}
                    <rect
                      x={padLeft}
                      y={padTop}
                      width={chartW}
                      height={chartH}
                      fill="none"
                      stroke={theme.border}
                      strokeWidth="1"
                    />
                  </svg>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* 4. 個別結果リスト */}
      {results && (
        <div style={{ marginTop: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <span
              style={{
                fontSize: "15px",
                fontWeight: "bold",
                color: theme.text,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Activity size={18} color="#8b5cf6" />
              分析対象イベント: {results.length}件
            </span>

            {/* 右側：コピー・出力ボタン群 */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={copyResultsToClipboard}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: "500",
                  background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                  border:
                    "1px solid " +
                    (isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                  color: isDark ? "#cbd5e1" : "#64748b",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <Copy size={14} />
                {!isMobile && "コピー"}
              </button>
              <button
                onClick={downloadResultsCSV}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: "500",
                  background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                  border:
                    "1px solid " +
                    (isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                  color: isDark ? "#cbd5e1" : "#64748b",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <Database size={14} />
                {!isMobile && "CSV出力"}
              </button>
            </div>
          </div>

          {/* 4. 個別結果リスト：アスペクト記号修正 ＋ 右側騰落率削除版 ＋ スクロール対応 */}
          <div
            style={{
              maxHeight: "600px",
              overflowY: "auto",
              overflowX: "auto",
              padding: "10px",
              background: isDark ? "rgba(0, 0, 0, 0.2)" : "rgba(240, 244, 248, 0.3)",
              border: "1px solid " + theme.border,
              borderRadius: "14px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {paginatedResults.map(function (r, i) {
              // 修正：全アスペクト記号をマッピング
              const ASPECT_MAP = {
                Conjunction: "☌",
                Opposition: "☍",
                Square: "□",
                Trine: "△",
                Sextile: "✶",
                "Semi-Sextile": "⚺",
                Quincunx: "ㅈ",
                Decile: "⊥",
                "Bi-Decile": "D2",
                "Tri-Decile": "D3",
                "Quadra-Decile": "D4",
                Novile: "N",
                "Bi-Novile": "N2",
                "Quad-Novile": "N4",
                "Semi-Square": "∠",
                Sesquiquadrate: "⚼",
                Septile: "S",
                "Bi-Septile": "S2",
                "Tri-Septile": "S3",
                Quintile: "Q",
                "Bi-Quintile": "bQ",
                Undecile: "U",
                "Semi-Decile": "SD",
                "Semi-Novile": "SN",
              };
              // 1. ヒットデータから記号を取得 (存在しない場合はマッピングから取得)
              const aspectSymbol =
                r.symbol ||
                (r.aspect ? ASPECT_MAP[r.aspect.name] : null) ||
                "-";

              // BULLISH/BEARISH インジケーター用の計算
              const ret30 =
                r.priceStats && typeof r.priceStats.returns[30] === "number"
                  ? r.priceStats.returns[30]
                  : null;

              return (
                <div
                  key={"hit-row-" + i}
                  style={{
                    background: isDark ? "rgba(255, 255, 255, 0.03)" : "#fff",
                    borderRadius: "10px",
                    border: "1px solid " + theme.border,
                    padding: "8px 15px",
                    display: "flex",
                    alignItems: "center",
                    minWidth: "fit-content", // 横に伸びすぎないように
                  }}
                >
                  {/* 左端：日付 + 時刻 (白字固定) */}
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDark ? "#ffffff" : "rgb(30, 41, 59)",
                      // 時刻を表示するため幅を少し調整
                      width: "120px",
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div>{r.date.toLocaleDateString("ja-JP")}</div>
                    <div style={{ fontSize: "10px", opacity: 0.8 }}>
                      Exact:{" "}
                      {r.date.toLocaleTimeString("ja-JP", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>

                  {/* 中央：アスペクト詳細レイアウト */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    {/* 惑星1：アイコン + 英語名 */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: "110px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "26px",
                          color: planetColors[r.planet1] || theme.text,
                        }}
                      >
                        {PLANET_SYMBOLS[r.planet1]}
                      </span>
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          color: theme.text,
                        }}
                      >
                        {r.planet1}
                      </span>
                    </div>

                    {/* アスペクト中央ブロック：記号 + 名称 */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: "110px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {/* 記号 (アイコン) の描画：ダッシュボード仕様の色(Hard=#e74c3c, Soft=#3498db)を適用 */}
                        <span
                          style={{
                            fontSize: "26px",
                            fontWeight: "normal",
                            color: (function () {
                              const hardAspects = [
                                "Conjunction",
                                "Square",
                                "Opposition",
                                "Semi-Square",
                                "Sesquiquadrate",
                                "Quincunx",
                              ];
                              const name = r.aspect ? r.aspect.name : "";
                              return hardAspects.indexOf(name) !== -1
                                ? "#e74c3c"
                                : "#3498db";
                            })(),
                          }}
                        >
                          {aspectSymbol}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: theme.text,
                          }}
                        >
                          {r.aspect ? r.aspect.name : ""}
                          {/* Squareの場合のみ、WAX/WAN判定ラベルを表示（font-weight: normal, font-size: 12px） */}
                          {r.aspect && r.aspect.name === "Square" && (
                            <span
                              style={{
                                fontWeight: "normal",
                                fontSize: "12px",
                                marginLeft: "4px",
                                color: isDark
                                  ? "rgba(255,255,255,0.7)"
                                  : "#64748b",
                              }}
                            >
                              {getSquareLabel(
                                r.planet1,
                                r.p1Lon,
                                r.planet2,
                                r.p2Lon,
                              )}
                            </span>
                          )}
                        </span>
                      </div>

                      {/* BULLISH/BEARISH インジケーター (データがある場合のみ表示) */}
                    </div>

                    {/* 惑星2：アイコン + 英語名 */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: "110px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "26px",
                          color: planetColors[r.planet2] || theme.text,
                        }}
                      >
                        {PLANET_SYMBOLS[r.planet2]}
                      </span>
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          color: theme.text,
                        }}
                      >
                        {r.planet2}
                      </span>
                    </div>
                  </div>

                  {/* 右端の騰落率表示 (---) はユーザーの指示により削除されました */}
                </div>
              );
            })}
          </div>

          {/* 5. ページネーション */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                marginTop: "40px",
              }}
            >
              <button
                disabled={currentPage === 1}
                onClick={() => {
                  setCurrentPage(currentPage - 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                style={pageBtnSt}
              >
                <ChevronLeft size={20} />
              </button>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#64748b",
                }}
              >
                {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => {
                  setCurrentPage(currentPage + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                style={pageBtnSt}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* --- 全画面ローディングオーバーレイ --- */}
      {isSearching && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.28)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)",
          }}
        >
          <Loader2
            size={48}
            style={{
              animation: "spin 1s linear infinite",
              color: "#64748b",
              marginBottom: "15px",
            }}
          />
          <div
            style={{ fontSize: "16px", fontWeight: "bold", color: "#64748b" }}
          >
            アスペクトを検索中...
          </div>
        </div>
      )}
    </div>
  );
}
